import { z } from 'zod';
import { ClientType, ClientStatus, PaymentTerms } from '../enums.js';
const stringTrim = z.string().trim();
export const clientContactInput = z.object({
    fullName: stringTrim.min(1).max(100),
    role: stringTrim.max(100).optional().nullable(),
    email: z.string().email().optional().nullable(),
    phone: stringTrim.max(40).optional().nullable(),
    isPrimary: z.boolean().default(false),
});
/**
 * Validazione conditional in base al tipo cliente.
 * - AZIENDA: P.IVA obbligatoria (società di capitali/persone con P.IVA propria)
 * - PIVA: P.IVA + CF (ditta individuale: il CF è quello del titolare)
 * - PRIVATO: solo CF obbligatorio (persona fisica senza P.IVA)
 * - PA: P.IVA + SDI obbligatori (Pubblica Amministrazione, codice SDI 6 caratteri)
 */
const clientBase = z.object({
    type: z.nativeEnum(ClientType).default(ClientType.AZIENDA),
    legalName: stringTrim.min(1).max(200),
    shortName: stringTrim.min(1).max(80),
    vat: stringTrim.max(20).optional().nullable(),
    taxCode: stringTrim.max(20).optional().nullable(),
    sdiCode: stringTrim.max(20).optional().nullable(),
    pec: z.string().email().optional().nullable().or(z.literal('')),
    // Recapiti dell'azienda, distinti da quelli del referente (contacts[]).
    // `.or(z.literal(''))` come la PEC: il form manda stringa vuota quando si
    // svuota il campo, e non deve fallire la validazione email.
    email: z.string().email().optional().nullable().or(z.literal('')),
    phone: stringTrim.max(40).optional().nullable(),
    sector: stringTrim.max(120).optional().nullable(),
    website: stringTrim.max(200).optional().nullable(),
    status: z.nativeEnum(ClientStatus).default(ClientStatus.LEAD),
    paymentTerms: z.nativeEnum(PaymentTerms).default(PaymentTerms.BONIFICO_30DF),
    docLanguage: z.enum(['it', 'en', 'bi']).default('it'),
    internalNotes: stringTrim.max(2000).optional().nullable(),
    addressLine: stringTrim.max(200).optional().nullable(),
    city: stringTrim.max(80).optional().nullable(),
    zip: stringTrim.max(10).optional().nullable(),
    province: stringTrim.max(4).optional().nullable(),
    country: stringTrim.max(60).default('IT'),
    accountManagerId: z.string().optional().nullable(),
    sinceYear: z.coerce.number().int().min(1900).max(2100).optional().nullable(),
    tags: z.array(z.string()).default([]),
    contacts: z.array(clientContactInput).default([]),
});
function validateClientType(data, ctx) {
    // Per le update partial, se `type` non è presente non possiamo validare condizionalmente.
    if (!data.type)
        return;
    const vat = data.vat?.trim();
    const tax = data.taxCode?.trim();
    const sdi = data.sdiCode?.trim();
    if (data.type === ClientType.AZIENDA && !vat) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vat'], message: 'P.IVA obbligatoria per Azienda' });
    }
    if (data.type === ClientType.PIVA) {
        if (!vat)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vat'], message: 'P.IVA obbligatoria per Ditta individuale' });
        if (!tax)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['taxCode'], message: 'Codice fiscale del titolare obbligatorio' });
    }
    if (data.type === ClientType.PRIVATO && !tax) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['taxCode'], message: 'Codice fiscale obbligatorio per Privato' });
    }
    if (data.type === ClientType.PA) {
        if (!vat && !tax)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vat'], message: 'P.IVA o CF della PA obbligatori' });
        if (!sdi)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sdiCode'], message: 'Codice IPA/SDI obbligatorio per PA (6 caratteri)' });
    }
}
export const createClientInput = clientBase.superRefine(validateClientType);
export const updateClientInput = clientBase.partial().superRefine(validateClientType);
export const clientDTO = z.object({
    id: z.string(),
    type: z.nativeEnum(ClientType),
    legalName: z.string(),
    shortName: z.string(),
    vat: z.string().nullable(),
    taxCode: z.string().nullable(),
    sdiCode: z.string().nullable(),
    pec: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    sector: z.string().nullable(),
    website: z.string().nullable(),
    status: z.nativeEnum(ClientStatus),
    paymentTerms: z.nativeEnum(PaymentTerms),
    docLanguage: z.string(),
    internalNotes: z.string().nullable(),
    addressLine: z.string().nullable(),
    city: z.string().nullable(),
    zip: z.string().nullable(),
    province: z.string().nullable(),
    country: z.string(),
    accountManager: z.object({ id: z.string(), firstName: z.string(), lastName: z.string(), initials: z.string() }).nullable(),
    sinceYear: z.number().nullable(),
    tags: z.array(z.object({ id: z.string(), name: z.string() })),
    contacts: z.array(z.object({
        id: z.string(),
        fullName: z.string(),
        role: z.string().nullable(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
        isPrimary: z.boolean(),
    })),
    projectsCount: z.number().int(),
    revenueYTD: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
//# sourceMappingURL=client.js.map